<template>
  <div>
    <!-- Bills with Received Amounts Table -->
    <b-card
      no-body
      class="mb-0"
    >
      <b-card-header class="bg-primary text-white pt-50 pb-50">
        <h4 class="mb-0 text-white">
          Received (Bills)
        </h4>
      </b-card-header>

      <!-- Error Alert -->
      <b-alert
        v-if="loadingError"
        variant="danger"
        show
        dismissible
        class="m-1 mb-0"
        @dismissed="loadingError = null"
      >
        <div class="alert-body">
          <feather-icon
            icon="AlertCircleIcon"
            class="mr-50"
          />
          <span>{{ loadingError }}</span>
        </div>
      </b-alert>

      <!-- Filters -->
      <b-card-body class="py-25 pr-0">
        <b-row class="align-items-center">
          <!-- All Filters in One Column -->
          <b-col
            cols="12"
            md="5"
            class="mb-25 px-0 mb-md-0"
          >
            <div
              class="d-flex flex-wrap align-items-center"
              style="gap: 0.5rem;"
            >
              <!-- Bill No Search -->
              <div style="width: 15%;">
                <b-form-input
                  v-model="filterBillNo"
                  size="sm"
                  placeholder="Bill No"
                />
              </div>

              <!-- Party Name Search -->
              <div style="width: 28%;">
                <b-form-input
                  v-model="filterPartyName"
                  size="sm"
                  placeholder="Party Name"
                />
              </div>

              <!-- Area Search -->
              <div style="width: 18%;">
                <b-form-input
                  v-model="filterArea"
                  size="sm"
                  placeholder="Area"
                />
              </div>

              <!-- Min Amount -->
              <div style="width: 15%;">
                <b-form-input
                  v-model.number="filterMinAmount"
                  type="number"
                  size="sm"
                  placeholder="Min Amt"
                  step="0.01"
                />
              </div>

              <!-- Max Amount -->
              <div style="width: 15%;">
                <b-form-input
                  v-model.number="filterMaxAmount"
                  type="number"
                  size="sm"
                  placeholder="Max Amt"
                  step="0.01"
                />
              </div>
            </div>
          </b-col>

          <!-- Payment Status Filter -->
          <b-col
            cols="12"
            md="3"
            class="mb-25 mb-md-0"
          >
            <custom-v-select
              v-model="filterPayStatus"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="payStatusOptions"
              label="text"
              placeholder="Payment Status"
              multiple
              :close-on-select="false"
              :deselect-from-dropdown="true"
              @input="handlePayStatusChange"
            />
          </b-col>

          <!-- Date Range -->
          <b-col
            cols="12"
            md="2"
            class="mb-25 mb-md-0"
          >
            <flat-picker
              v-model="dateRange"
              placeholder="Bill Date Range"
              :config="{ mode: 'range' }"
            />
          </b-col>

          <!-- Action Buttons -->
          <b-col
            cols="12"
            md="2"
            class="d-flex align-items-center justify-content-md-end"
          >
            <b-button
              variant="outline-primary"
              size="sm"
              class="mr-25"
              :disabled="loading"
              @click="refreshAll"
            >
              <feather-icon
                icon="RefreshCwIcon"
                size="14"
                class="mr-25"
              />
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="mr-25"
              @click="clearFilters"
            >
              Clear
            </b-button>
            <b-button
              v-b-tooltip.hover.top="`Mark ${selectedBills.length} bill${selectedBills.length !== 1 ? 's' : ''} pack, pick and delivered`"
              :variant="selectedBills.length > 0 ? 'success' : 'outline-secondary'"
              size="sm"
              class="mr-25"
              :disabled="selectedBills.length === 0"
              @click="handleMarkAsDelivered"
            >
              <feather-icon
                icon="TruckIcon"
                size="14"
              />
            </b-button>
            <b-button
              v-b-tooltip.hover
              title="Toggle Additional Filters"
              variant="outline-primary"
              size="sm"
              @click="showAdditionalFilters = !showAdditionalFilters"
            >
              <feather-icon
                :icon="showAdditionalFilters ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="14"
              />
            </b-button>
          </b-col>
        </b-row>

        <!-- Additional Filters Collapse -->
        <b-collapse
          v-model="showAdditionalFilters"
          class="px-0"
        >
          <b-row class="align-items-center">
            <!-- Received Date Range Filter -->
            <b-col
              cols="12"
              md="3"
              class="mb-0 mb-md-0 px-0"
            >
              <flat-picker
                v-model="receivedDateRange"
                placeholder="Received Date Range"
                :config="{ mode: 'range' }"
              />
            </b-col>
          </b-row>
        </b-collapse>
      </b-card-body>

      <!-- Table -->
      <div class="table-responsive">
        <table class="table table-bordered table-sm mb-0">
          <thead>
            <tr class="bg-light">
              <th style="width: 2%;">
                <b-form-checkbox
                  v-model="isAllBillSelected"
                  :disabled="paginatedBills.length === 0"
                  @change="handleAllBillSelectChange"
                />
              </th>
              <th style="width: 10%;">
                Bill No
              </th>
              <th style="width: 13%;">
                Party Name
              </th>
              <th style="width: 10%;">
                Area
              </th>
              <th style="width: 8%;">
                Pay Status
              </th>
              <th
                style="width: 7%;"
                class="text-right"
              >
                Amt (₹)
                <div class="font-weight-bold text-success">
                  ₹{{ totalAmount.toFixed(2) }}
                </div>
              </th>
              <th
                style="width: 8%;"
                class="text-right"
              >
                Due Amt (₹)
                <div class="font-weight-bold text-danger">
                  ₹{{ totalDueAmount.toFixed(2) }}
                </div>
              </th>

              <th
                style="width: 8%;"
                class="text-right"
              >
                Received (₹)
              </th>
              <th style="width: 10%;">
                Payment Mode
              </th>
              <th style="width: 10%;">
                Remarks
              </th>
              <th style="width: 10%;">
                Received Date
              </th>
              <th style="width: 4%;">
                Actions
              </th>
            </tr>
          </thead>
          <!-- Empty State -->
          <tbody v-if="filteredBills.length === 0 && !loading">
            <tr>
              <td
                colspan="13"
                class="text-center text-muted py-2"
              >
                No bills found
              </td>
            </tr>
          </tbody>

          <!-- Loading State -->
          <tbody v-if="loading">
            <tr>
              <td
                colspan="13"
                class="text-center py-2"
              >
                <b-spinner
                  small
                  class="mr-50"
                />
                Loading bills...
              </td>
            </tr>
          </tbody>
          <!-- Bill Rows with Received Amounts -->
          <received-bill-amount-item
            v-for="(bill, index) in paginatedBills"
            :key="`bill-${bill.id}-${bill.updateKey}`"
            :bill="bill"
            :bill-index="(currentPage - 1) * perPage + index + 1"
            :payment-mode-options="paymentModeOptions"
            :borrower-options="borrowerOptions"
            :mr-options="mrOptions"
            :payment-status-options="paymentStatusOptions"
            :is-selected="selectedBills.includes(bill.id)"
            @data-updated="handleDataRefresh"
            @selection-changed="handleBillSelection"
          />
        </table>
      </div>

      <!-- Pagination -->
      <detailed-pagination
        v-if="!loading && !loadingError"
        :total-items="filteredBills.length"
        :per-page="perPage"
        :current-page="currentPage"
        @page-changed="(page) => {currentPage = page}"
      />
    </b-card>

    <!-- Mark Pack Pick Delivery Modal -->
    <mark-pack-pick-delivery-modal
      v-if="confirmPickAndDeliverModal"
      v-model="confirmPickAndDeliverModal"
      :operation-users="operationUsers"
      :bill-delivery-ids="selectedDeliveryBills"
      :bill-rg-status-ids="selectedBillsWithStatus"
      @updated="clearBillSelection"
    />
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BSpinner,
  BAlert,
  BFormCheckbox,
  BCollapse,
  VBTooltip,
} from 'bootstrap-vue'
import FlatPicker from '@/components/UI/FlatPicker.vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import axios from 'axios'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import getParsedQueryString from '@/utils/headerQueryParser'
import DetailedPagination from '@/components/UI/DetailedPagination.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import MarkPackPickDeliveryModal from '@/components/Operations/RackGroupReports/MarkPackPickDeliveryModal.vue'
import ReceivedBillAmountItem from './ReceivedBillAmountItem.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BSpinner,
    BAlert,
    BFormCheckbox,
    BCollapse,
    FlatPicker,
    CustomVSelect,
    DetailedPagination,
    ReceivedBillAmountItem,
    MarkPackPickDeliveryModal,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [dateMixin, formatMixin],
  data() {
    return {
      // Date Range - Set today's date by default
      dateRange: null,

      // Additional Filters
      showAdditionalFilters: false,
      receivedDateRange: null,

      // Client-side Filters
      filterBillNo: '',
      filterPartyName: '',
      filterArea: '',
      filterMinAmount: null,
      filterMaxAmount: null,

      // Server-side Filter
      filterPayStatus: [{ value: 'unpaid', text: 'Unpaid', id: 'unpaid' },
        { value: 'partially_paid', text: 'Partially Paid', id: 'Partially_paid' }],

      // Data
      allBills: [], // All fetched bills
      borrowerOptions: [],
      mrOptions: [],
      paymentModeOptions: [
        { value: 'cash', text: 'Cash' },
        { value: 'upi', text: 'UPI' },
        { value: 'cheque', text: 'Cheque' },
        { value: 'rgts', text: 'RGTS' },
        { value: 'credit_note', text: 'Credit Note' },
        { value: 'challan_converted_bill', text: 'Challan Converted Bill' },
        { value: 'rec_by_acc_dept', text: 'Rec. By Account Dept' },
        { value: 'loan', text: 'Loan' },
        { value: 'mr_handover', text: 'MR Handover' },
      ],
      paymentStatusOptions: [
        { value: 'paid', text: 'Paid' },
        { value: 'unpaid', text: 'Unpaid' },
        { value: 'partially_paid', text: 'Partially Paid' },
      ],
      payStatusOptions: [
        { value: 'paid', text: 'Paid', id: 'paid' },
        { value: 'unpaid', text: 'Unpaid', id: 'unpaid' },
        { value: 'partially_paid', text: 'Partially Paid', id: 'Partially_paid' },
      ],

      // Loading States
      loading: false,

      // Error State
      loadingError: null,

      // Pagination
      currentPage: 1,
      perPage: 50,

      // Selection
      selectedBills: [],
      isAllBillSelected: false,

      // Mark Pack Pick Delivery
      confirmPickAndDeliverModal: false,
      operationUsers: [],
    }
  },
  computed: {
    filteredBills() {
      let filtered = [...this.allBills]

      // Filter by Bill No (client-side)
      if (this.filterBillNo) {
        const searchTerm = this.filterBillNo.trim().toLowerCase()
        filtered = filtered.filter(bill => {
          const billNo = bill.bill_no ? String(bill.bill_no).trim().toLowerCase() : ''
          return billNo.includes(searchTerm)
        })
      }

      // Filter by Party Name (client-side)
      if (this.filterPartyName) {
        const searchTerm = this.filterPartyName.toLowerCase()
        filtered = filtered.filter(bill => {
          const partyName = this.getPartyName(bill.party__identifier).toLowerCase()
          return partyName.includes(searchTerm)
        })
      }

      // Filter by Area (client-side)
      if (this.filterArea) {
        const searchTerm = this.filterArea.toLowerCase()
        filtered = filtered.filter(bill => {
          const area = this.getPartyArea(bill.party__identifier).toLowerCase()
          return area.includes(searchTerm)
        })
      }

      // Filter by Min Amount (client-side)
      if (this.filterMinAmount !== null && this.filterMinAmount !== '') {
        filtered = filtered.filter(bill => (bill.amount || 0) >= this.filterMinAmount)
      }

      // Filter by Max Amount (client-side)
      if (this.filterMaxAmount !== null && this.filterMaxAmount !== '') {
        filtered = filtered.filter(bill => (bill.amount || 0) <= this.filterMaxAmount)
      }

      return filtered
    },

    paginatedBills() {
      const start = (this.currentPage - 1) * this.perPage
      const end = start + this.perPage
      return this.filteredBills.slice(start, end)
    },

    // Total Amount - sum of all bill amounts in filtered bills
    totalAmount() {
      return this.filteredBills.reduce((sum, bill) => sum + (bill.amount || 0), 0)
    },

    // Total Due Amount - total amount minus total received amounts
    totalDueAmount() {
      return this.filteredBills.reduce((sum, bill) => {
        const billAmount = bill.amount || 0
        const totalReceived = this.getTotalReceivedAmount(bill)
        return sum + (billAmount - totalReceived)
      }, 0)
    },

    // Selected delivery bill IDs
    selectedDeliveryBills() {
      return this.allBills
        .filter(bill => this.selectedBills.includes(bill.id))
        .map(bill => bill.bill_delivery_info_id)
        .filter(id => id !== null && id !== undefined)
    },

    // Selected bills with rack group status IDs
    selectedBillsWithStatus() {
      return this.allBills
        .filter(bill => this.selectedBills.includes(bill.id))
        .map(bill => bill.rack_group_status_id)
        .filter(id => id !== null && id !== undefined)
    },
  },
  watch: {
    dateRange() {
      this.fetchBills()
    },
    receivedDateRange() {
      this.fetchBills()
    },
    selectedBills(newVal) {
      if (this.paginatedBills.length > 0 && newVal.length === this.paginatedBills.length) {
        this.isAllBillSelected = true
      } else {
        this.isAllBillSelected = false
      }
    },
  },
  mounted() {
    this.fetchBorrowerOptions()
    this.fetchOperationUsers()
    this.fetchMrOptions()
    this.fetchBills()
  },
  methods: {
    // API Calls
    async fetchBorrowerOptions() {
      try {
        const response = await axios.get('/operations/borrowers?per_page=500')
        this.borrowerOptions = response.data && response.data.results.map(borrower => ({
          value: borrower.id,
          text: borrower.contact_no ? `${borrower.name} - ${borrower.contact_no}` : borrower.name,
        }))
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to load borrowers'
      }
    },
    async fetchMrOptions() {
      try {
        const response = await axios.get('/mr?per_page=500')
        this.mrOptions = response.data && response.data.results.map(item => {
          // Find primary mobile number
          const primaryMobile = item.mr_mobiles?.find(mobile => mobile.is_primary)?.mobile || ''

          // Create display text with name and primary mobile
          const displayText = primaryMobile
            ? `${item.name} ( ${primaryMobile} )`
            : item.name

          return {
            value: item.id,
            text: displayText,
          }
        })
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to load mr'
      }
    },
    fetchData() {
      this.fetchBills()
    },
    // API Calls
    async fetchBills() {
      this.loading = true
      this.loadingError = null
      this.currentPage = 1 // Reset to first page on fetch
      this.selectedBills = [] // Clear selection

      try {
        const params = {
          per_page: 500, // Fetch 500 records for client-side filtering
        }

        if (this.dateRange) {
          const [startDate, endDate] = this.dateRange.split(' to ')
          if (startDate && endDate) {
            params.from_date = startDate
            params.to_date = endDate
          }
        }
        if (this.receivedDateRange) {
          const [startDate, endDate] = this.receivedDateRange.split(' to ')
          if (startDate && endDate) {
            params.received_date_from = startDate
            params.received_date_to = endDate
          }
        }

        if (this.filterPayStatus && this.filterPayStatus.length > 0) {
          params.pay_status = this.filterPayStatus.map(item => item.value || item)
        }
        params.cash_bills = true

        const response = await axios.get(`/operations/bills?${getParsedQueryString(params)}`)
        // Map with updateKey to force reactivity
        this.allBills = response.data.results.map(bill => ({
          ...bill,
          statusCount: this.calculateStatusCount(bill.bill_delivery_status, bill.rack_group_status),
          updateKey: Date.now(), // Force reactivity by adding unique key
        }))
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to load bills. Please try again.'
        this.allBills = []
      } finally {
        this.loading = false
      }
    },

    // Handler for payment status change
    handlePayStatusChange() {
      this.fetchBills()
    },

    // Data refresh handlers
    async handleDataRefresh() {
      await this.fetchBills()
      this.$emit('data-updated')
    },

    // Filter helpers
    refreshAll() {
      this.fetchBills()
    },

    clearFilters() {
      this.filterBillNo = ''
      this.filterPartyName = ''
      this.filterArea = ''
      this.filterMinAmount = null
      this.filterMaxAmount = null
      this.filterPayStatus = []
      this.receivedDateRange = null
      this.showAdditionalFilters = false
      this.currentPage = 1
      this.selectedBills = []
      this.refreshAll()
    },

    // Helper methods
    getPartyName(identifier) {
      if (!identifier) return '-'
      const parts = identifier.split('|')
      return parts[0]?.trim() || '-'
    },

    getPartyArea(identifier) {
      if (!identifier) return '-'
      const parts = identifier.split('|')
      return parts[1]?.trim() || '-'
    },

    // Calculate total received amount for a bill
    getTotalReceivedAmount(bill) {
      if (!bill.received_amounts || !Array.isArray(bill.received_amounts)) {
        return 0
      }
      return bill.received_amounts.reduce((sum, received) => sum + (received.received_amount || 0), 0)
    },

    calculateStatusCount(deliveryStatus, rackGroupStatus) {
      const delivery = (deliveryStatus || '').toLowerCase()
      const rackGroup = (rackGroupStatus || '').toLowerCase()
      if (delivery === 'delivered') return 5
      if (delivery === 'picked') return 4
      if (rackGroup !== 'created' && (delivery === 'created' || delivery === 'confirmed' || delivery === '')) return 3
      if (delivery === 'confirmed' || delivery === 'created') return 2
      return 1
    },

    // Selection handlers
    handleAllBillSelectChange(isChecked) {
      this.selectedBills = isChecked
        ? this.paginatedBills.map(bill => bill.id)
        : []
    },

    handleBillSelection({ billId, isSelected }) {
      if (isSelected) {
        if (!this.selectedBills.includes(billId)) {
          this.selectedBills.push(billId)
        }
      } else {
        this.selectedBills = this.selectedBills.filter(id => id !== billId)
      }
    },

    // Mark as Delivered
    handleMarkAsDelivered() {
      this.confirmPickAndDeliverModal = true
    },

    async clearBillSelection() {
      this.selectedBills = []
      await this.fetchBills()
    },

    // Fetch operation users
    async fetchOperationUsers() {
      try {
        const { data } = await axios.get('operations/operation-users')
        this.operationUsers = data
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.response?.data?.message || 'Failed to fetch operation users',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
  },
}
</script>

<style scoped>
/* Minimal vertical spacing utilities */
.py-25 {
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
}

.py-50 {
  padding-top: 0.5rem !important;
  padding-bottom: 0.5rem !important;
}

.pb-50 {
  padding-bottom: 0.5rem !important;
}

.mb-25 {
  margin-bottom: 0.25rem !important;
}

.mb-50 {
  margin-bottom: 0.5rem !important;
}

.mt-25 {
  margin-top: 0.25rem !important;
}

.mt-50 {
  margin-top: 0.5rem !important;
}

.mr-25 {
  margin-right: 0.25rem !important;
}

.mr-50 {
  margin-right: 0.5rem !important;
}

.pr-25 {
  padding-right: 0.25rem !important;
}

/* Remove horizontal gutters for tighter layout */
.gx-50 {
  margin-right: -0.25rem;
  margin-left: -0.25rem;
}

/* Border danger for validation */
.border-danger {
  border-color: #ea5455 !important;
}

/* Compact table styling */
.table-sm td,
.table-sm th {
  padding: 0.3rem;
  vertical-align: middle;
}

.table-bordered {
  border: 1px solid #dee2e6;
}

.table-bordered td,
.table-bordered th {
  border: 1px solid #dee2e6;
}

/* Small form controls */
.form-control-sm {
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

/* Responsive table */
.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .justify-content-md-end {
    justify-content: flex-start !important;
  }
}
</style>
